// AuthAdapter — isolated seam that wires the REAL SDIP authentication contract
// through the existing transport bridge. It performs no fake/demo auth and never
// defaults a membership tier. /auth/me is the single source of truth for profile
// and tier. Tokens are never logged, placed in URLs, or shown in the UI.
import { apiRequest, API_BASE } from "./client";
import { MembershipTier } from "@/types/api";

const TOKEN_KEY = "sdip.token";
const REFRESH_KEY = "sdip.refresh";
const SESSION_KEY = "sdip.session";

export interface SessionUser {
  email?: string;
  displayName?: string;
  tier?: MembershipTier | null;
  preview: boolean;        // true => local preview session using public real data, no authenticated state
  authenticated?: boolean; // true => real token-based session validated via /auth/me
}

export type LoginResult =
  | { ok: true; user: SessionUser }
  | { ok: false; reason: "invalid" | "network" | "unavailable" };

// ---- token/session storage ------------------------------------------------
// STORAGE ASSUMPTION: the bridge strips upstream Set-Cookie, so an httpOnly
// cookie session cannot reach the browser — the session is carried by a Bearer
// token that must be stored client-side. We persist it in localStorage so the
// session survives reloads; on every load it is re-validated against /auth/me
// (and refreshed via /auth/refresh on 401). Tradeoff: localStorage is readable
// by same-origin JS (XSS exposure) — acceptable here because there are no
// ambient cookies and no credentials mode; hardening to in-memory access token
// can follow once the exact token lifetime is confirmed with real credentials.
export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string | null) {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch {}
}
function getRefresh(): string | null {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}
function setRefresh(t: string | null) {
  try { t ? localStorage.setItem(REFRESH_KEY, t) : localStorage.removeItem(REFRESH_KEY); } catch {}
}
export function getSession(): SessionUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch { return null; }
}
export function setSession(u: SessionUser | null) {
  try { u ? localStorage.setItem(SESSION_KEY, JSON.stringify(u)) : localStorage.removeItem(SESSION_KEY); } catch {}
}

// ---- response field extraction (contract-tolerant, no logging) ------------
// The exact success-response field names are not yet confirmed, so we accept the
// common standard names. The token MUST arrive in the JSON body — a token
// delivered only via a response header/Set-Cookie would be stripped by the
// bridge and cannot be wired without a bridge change (out of scope).
function unwrap(resp: any): any {
  return resp && typeof resp.data === "object" && resp.data ? resp.data : resp;
}
function pick(obj: any, keys: string[]): string | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of keys) if (obj[k] != null && obj[k] !== "") return String(obj[k]);
  return undefined;
}
function extractToken(resp: any): string | undefined {
  const s = unwrap(resp);
  return pick(s, ["access_token", "accessToken", "token", "access", "jwt", "id_token"]);
}
function extractRefresh(resp: any): string | undefined {
  const s = unwrap(resp);
  return pick(s, ["refresh_token", "refreshToken", "refresh"]);
}
function normalizeTier(v: unknown): MembershipTier | null {
  if (v == null) return null;
  const s = String(v).toUpperCase();
  return (["FREE", "PRO", "ELITE", "PREMIUM"] as const).includes(s as MembershipTier)
    ? (s as MembershipTier)
    : null;
}

// ---- /auth/me : the single source of truth for profile + tier -------------
export async function fetchMe(): Promise<SessionUser | null> {
  try {
    const me = await apiRequest<any>("/auth/me", { method: "GET", auth: true, retries: 0 });
    const s = unwrap(me);
    // Canonical membership field is `plan` (users.plan_id -> plans.code), a
    // string like "FREE". Tolerate an object form too. No default/fabrication.
    const rawPlan: any = s?.plan;
    const planVal = rawPlan && typeof rawPlan === "object" ? (rawPlan.code ?? rawPlan.tier) : rawPlan;
    const tier = normalizeTier(
      planVal ?? s?.tier ?? s?.membership ?? s?.plan_code ??
      s?.membership_tier ?? s?.subscription?.tier ?? s?.subscription?.plan?.code,
    );
    return {
      email: s?.email ?? s?.username ?? undefined,
      displayName: s?.displayName ?? s?.display_name ?? s?.name ?? s?.email ?? undefined,
      tier,
      preview: false,
      authenticated: true,
    };
  } catch {
    return null; // never fabricate profile/tier on failure
  }
}

// ---- login ----------------------------------------------------------------
export async function login(identifier: string, password: string): Promise<LoginResult> {
  let resp: any;
  try {
    resp = await apiRequest<any>("/auth/login", {
      method: "POST",
      body: { identifier, password },
      auth: false,
      retries: 0,
    });
  } catch (e: any) {
    const status = e?.status;
    if (status === 401 || status === 403 || status === 422 || status === 400) return { ok: false, reason: "invalid" };
    return { ok: false, reason: "network" };
  }
  const token = extractToken(resp);
  if (!token) return { ok: false, reason: "unavailable" };
  setToken(token);
  const refresh = extractRefresh(resp);
  if (refresh) setRefresh(refresh);
  const user = await fetchMe();
  if (!user) { setToken(null); setRefresh(null); return { ok: false, reason: "invalid" }; }
  setSession(user);
  return { ok: true, user };
}

// ---- register : creates a FREE user; never starts a trial ----------------
export type RegisterResult =
  | { ok: true }
  | { ok: false; reason: "exists" | "invalid" | "rate_limited" | "network" };

export async function register(email: string, password: string, fullName?: string): Promise<RegisterResult> {
  const body: Record<string, string> = { email, password };
  if (fullName && fullName.trim()) body.full_name = fullName.trim();
  try {
    await apiRequest<any>("/auth/register", { method: "POST", body, auth: false, retries: 0 });
    return { ok: true };
  } catch (e: any) {
    const status = e?.status;
    if (status === 409) return { ok: false, reason: "exists" };
    if (status === 422 || status === 400) return { ok: false, reason: "invalid" };
    if (status === 429) return { ok: false, reason: "rate_limited" };
    return { ok: false, reason: "network" };
  }
}

// ---- refresh (called by the API client on a 401 for authed requests) ------
export async function tryRefresh(): Promise<boolean> {
  const rt = getRefresh();
  if (!rt) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) { setRefresh(null); return false; }
    const data = await res.json().catch(() => null);
    const token = extractToken(data);
    if (!token) return false;
    setToken(token);
    const nr = extractRefresh(data);
    if (nr) setRefresh(nr);
    return true;
  } catch {
    return false;
  }
}

// Local preview session: explores REAL public intelligence data only. It NEVER
// fabricates a membership tier or authenticated state — tier stays null.
export function startPreview(_email?: string): SessionUser {
  const user: SessionUser = { email: undefined, displayName: "Genel görünüm", tier: null, preview: true, authenticated: false };
  setSession(user);
  return user;
}

// ---- logout : call backend (best effort) then clear local state ------------
export async function logout() {
  const token = getToken();
  if (token) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
    } catch { /* clear locally regardless */ }
  }
  setToken(null);
  setRefresh(null);
  setSession(null);
}
